import React from "react"
import { RecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ModalityGraphGroupConfig } from "../../../../Types/ModalityGraphGroup"
import { getVisibleGraphs } from "../visibleGraphs"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { useMobergMenu } from "../../../../../../../../Hooks/useMobergMenu"
import { useState } from "react"
import { TraceDataConfig } from "../../../../Types/Trace"
import { MobergContextMenuItem } from "../../../../../../../../Components/MobergMenu/MobergContextMenuItem"
import { MdVisibility, MdVisibilityOff } from "react-icons/md"
import { MobergIconSize } from "../../../../../../../../Moberg"
import { MobergMenuDivider } from "../../../../../../../../Components/MobergMenu/MobergMenuDivider"

export const useTraceVisibilityContextMenu = (atom: RecoilState<ModalityGraphGroupConfig>) => {
    const setConfig = useSetRecoilState(atom)
    const { sourceModalities } = useRecoilValue(currentPatientFileInfoAtom)
    const [traceVisibilityMenuData, setTraceVisibilityMenuData] = useState<{graphId: string, traceConfig: TraceDataConfig}>()
    const menu = useMobergMenu({ placement: "right-start", isContextMenu: true })

    const setTraceVisibility = (getVisibility: (graphId: string, traceId: string, value: boolean) => boolean) => {
        setConfig(previous => {
            const newGraphs = previous.graphs.map(graph => ({
                ...graph,
                traces: graph.traces.map(trace => ({
                    ...trace, 
                    isVisible: getVisibility(graph.id, trace.id, trace.isVisible ?? true)
                }))
            }))

            // If there are no visible graphs, make sure "hideEmptyGraphs" is false, otherwise, no change
            const noVisibleGraphs = getVisibleGraphs(newGraphs, sourceModalities, true).length === 0
            return {
                ...previous, 
                graphs: newGraphs, 
                hideEmptyGraphs: noVisibleGraphs ? false : previous.hideEmptyGraphs 
            }
        })
        menu.close()
    }

    const toggleTraceVisibility = (graphId: string, traceId: string) => {
		setTraceVisibility((_graphId, _traceId, isVisible) => graphId === _graphId && traceId === _traceId ? !isVisible : isVisible)
	}

    const openTraceVisibilityContextMenu = (event: React.MouseEvent, traceConfig: TraceDataConfig, graphId: string) => {
        setTraceVisibilityMenuData({ graphId, traceConfig })
        menu.open(event)
    }

    const TraceVisibilityContextMenu = (
        <menu.MobergMenu>
            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => (
                graphId === traceVisibilityMenuData?.graphId && traceId === traceVisibilityMenuData?.traceConfig.id 
                    ? true 
                    : graphId === traceVisibilityMenuData?.graphId 
                        ? false 
                        : currentVisibility
                ))}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Only show {traceVisibilityMenuData?.traceConfig?.name}
            </MobergContextMenuItem>

            <MobergMenuDivider />

            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => graphId === traceVisibilityMenuData?.graphId ? true : currentVisibility)}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Show all
            </MobergContextMenuItem>
            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => graphId === traceVisibilityMenuData?.graphId ? false : currentVisibility)}
                icon={<MdVisibilityOff size={MobergIconSize.SMALL} />}>
                Hide all
            </MobergContextMenuItem>
        </menu.MobergMenu>
    )

    return { TraceVisibilityContextMenu, openTraceVisibilityContextMenu, setTraceVisibility, toggleTraceVisibility }
}
